import React, { useEffect, useState } from 'react';
import { checkIsLoaded, checkIsLoading } from '../../lib/utils/checkLoadingStatus';
import {
  getPersonCategoryDictionary,
  getPersonStatusesDictionary,
  getPersonTypesDictionary,
  getSubwayStationDictionary,
} from '../../store/slices/dictionariesSlice/dictionariesSlice';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { Col, Modal, Row, Spin } from 'antd';
import axios, { AxiosResponse } from 'axios';
import { profileRoute } from '../../lib/routes/profile';
import moment from 'moment';
import { toYearMonthDayLineFormat } from '../../lib/utils/formatDate';
import {
  fetchMeUserFull,
  getPersonAvatar,
  getPersonProfileShortInfo,
} from '../../store/slices/personsSlice/personsSlice';
import { createEmailConfirmationRoute, getPersonAvatarRoute } from '../../lib/routes/persons';
import { changeUserPasswordRoute } from '../../lib/routes/users';
import { message } from 'antd/es';
import { getSubscriptions } from '../../store/slices/subscriptionsSlice/subscriptionsSlice';
import { useDictionaries } from '../../lib/utils/useDictionaries';
import { getProjects } from '../../store/slices/projectsSlice/projectsSlice';
import { updateSubscriptionsRoute } from '../../lib/routes/subscriptions';
import { FieldsSubscriptions, SubscriptionsUpdateParams } from '../../lib/interfaces/Subscriptions';
import './UserProfileContainer.less';
import { useEmailCooldown } from '../../lib/utils/useEmailCooldown';
import { useEmailConfirmationPolling } from '../../lib/utils/useEmailConfirmationPolling';
import { Navigate, Route, Routes } from 'react-router-dom';
import { UserProfileNavbar } from '../../components/UserProfile/UserProfileNavbar/UserProfileNavbar';
import UserProfileInfo from '../../components/UserProfile/UserProfileInfo/UserProfileInfo';
import UserProfileSubscriptions from '../../components/UserProfile/UserProfileSubscriptions/UserProfileSubscriptions';
import UserProfileChangePassword from '../../components/UserProfile/UserProfileChangePassword/UserProfileChangePassword';
import AuthService from '../../services/AuthService';

function UserProfileContainer() {
  const dispatch = useAppDispatch();
  const { personTypesLoading, personStatusesLoading, personCategoriesLoading, subwayStationLoading } = useAppSelector(
    (state) => state.dictionaries
  );
  const { meUserFull, personAvatar, personAvatarLoading, personProfileShortInfo } = useAppSelector(
    (state) => state.persons
  );
  const { properties } = useAppSelector((state) => state.root);

  const currentUser = AuthService.getCurrentUser();

  const { projectsLoading } = useAppSelector((state) => state.projects);

  const { loadDictionaries } = useDictionaries();

  const { isDisabled: isEmailConfirmDisabled, startCooldown, endCooldown } = useEmailCooldown();
  const { startPolling } = useEmailConfirmationPolling();

  const loadAvatar = () => {
    if (meUserFull?.id) {
      dispatch(getPersonAvatar(meUserFull?.id));
    }
  };

  const handleNewAvatar = (file, route) => {
    axios
      .post(
        route(),
        { file },
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      )
      .then(() => {
        loadAvatar();
      });
  };

  useEffect(() => {
    /*   dispatch(getPersonProfileShortInfo());*/
    if (currentUser?.user_id) dispatch(getSubscriptions(currentUser.user_id));
  }, []);

  useEffect(() => {
    if (properties?.exclude_Status) {
      if (!checkIsLoaded(personStatusesLoading))
        dispatch(getPersonStatusesDictionary({ excludeIds: [properties?.exclude_Status] }));
    }
  }, [properties?.exclude_Status]);

  useEffect(() => {
    if (!checkIsLoaded(personTypesLoading)) dispatch(getPersonTypesDictionary());
    if (!checkIsLoaded(projectsLoading)) dispatch(getProjects({}));
    if (!checkIsLoaded(personCategoriesLoading)) dispatch(getPersonCategoryDictionary());
    if (!checkIsLoaded(subwayStationLoading)) dispatch(getSubwayStationDictionary());
    loadDictionaries(['app_subject']);
  }, []);

  const handleEditProfile = (fields, isEditField, callbackSuccess, callbackError) => {
    const params = {
      firstName: fields.firstName || personProfileShortInfo?.firstName,
      lastName: fields.lastName || personProfileShortInfo?.lastName,
      middleName: fields.noMiddleName ? undefined : fields.middleName || personProfileShortInfo?.middleName,

      email: fields.email?.toLowerCase() || personProfileShortInfo?.email,
      phone: fields.phone || personProfileShortInfo?.phone,
      birthDate: fields.birthDate
        ? toYearMonthDayLineFormat(moment(fields.birthDate, 'DD.MM.YYYY'))
        : personProfileShortInfo?.birthDate,
      address: {
        id: personProfileShortInfo?.address?.id,
        subwayStationId: fields.subwayStationId || personProfileShortInfo?.address?.subwayStationId,
        unstructuredAddress: fields.unstructuredAddress || personProfileShortInfo?.address?.unstructuredAddress,
        apartment: fields.apartment || personProfileShortInfo?.address?.apartment,
      },
    };

    axios
      .patch(profileRoute(), params)
      .then(() => {
        dispatch(getPersonProfileShortInfo()).then(() => {
          callbackSuccess();
          message.destroy();
          message.success('Изменения сохранены');
        });
        if (String(fields.email) !== String(meUserFull?.email)) endCooldown();
      })
      .catch(() => {
        callbackError();
        message.destroy();
        message.error('Произошла ошибка. Пожалуйста, попробуйте позднее');
      });
  };

  const handleChangePassword = (args: { oldPassword: string; newPassword: string }) => {
    const { newPassword, oldPassword } = args;
    if (currentUser?.user_id) {
      return axios
        .patch(changeUserPasswordRoute(currentUser.user_id), {
          newPassword,
          oldPassword,
        })
        .then((res) => {
          message.destroy();
          message.success('Пароль успешно изменён');
          return res;
        })
        .catch((err) => {
          if (err.response.data.code !== 1000) {
            message.destroy();
            message.error('Произошла ошибка. Пожалуйста, попробуйте позднее');
            return err;
          }
          return err;
        });
    }
  };

  const handleEditSubscriptions = (fields: FieldsSubscriptions): Promise<AxiosResponse> => {
    const params: SubscriptionsUpdateParams = {
      fromDate: moment().format(),
      userId: meUserFull && meUserFull.userId,
      notificationTypeId: 15,
      subscriptionCondition: {
        ...fields,
      },
    };

    if (
      params.subscriptionCondition?.projectIds?.length === 0 &&
      params.subscriptionCondition?.appSubjectIds?.length === 0 &&
      params.subscriptionCondition?.personCategoryIds?.length === 0
    ) {
      delete params.subscriptionCondition;
    }

    return axios
      .post(updateSubscriptionsRoute(), params)
      .then((res) => {
        if (currentUser?.user_id) dispatch(getSubscriptions(currentUser.user_id));

        message.success(
          {
            content: 'Изменения сохранены',
          },
          3
        );

        return res;
      })
      .catch((err) => {
        message.error(
          {
            content: 'Произошла ошибка. Пожалуйста, повторите попытку позднее',
          },
          3
        );
        return err;
      });
  };

  const handleConfirmEmail = () => {
    if (isEmailConfirmDisabled) return;

    if (currentUser?.user_id) {
      axios
        .post(createEmailConfirmationRoute())
        .then((res) => {
          Modal.destroyAll();
          Modal.success({
            width: 544,
            icon: null,
            centered: true,
            content: (
              <Row>
                <Col>Письмо отправлено.</Col>
                <Col>Подтвердите Вашу электронную почту в течение 15 минут.</Col>
              </Row>
            ),
            className: 'modal_confirm_email',
          });

          // Блокировка кнопки
          startCooldown();
          // Запуск опроса проверки подтверждена ли почта
          startPolling();

          return res;
        })
        .catch((error) => {
          Modal.destroyAll();
          Modal.error({
            title: error.response.data.message,
            width: 544,
            centered: true,
            icon: '',
            className: 'modal_confirm_email',
          });
        });
    }
  };

  return (
    <Spin
      className="container_height"
      spinning={checkIsLoading(personTypesLoading, personStatusesLoading, subwayStationLoading)}
    >
      {checkIsLoaded(personTypesLoading, personStatusesLoading, subwayStationLoading) && (
        <div className="user-profile">
          <UserProfileNavbar />

          <Routes>
            <Route path="" element={<Navigate to={`info`} />} />
            <Route
              path="info"
              element={
                <UserProfileInfo
                  image={personAvatar}
                  handleNewImage={handleNewAvatar}
                  isImageLoading={checkIsLoading(personAvatarLoading)}
                  handleConfirmEmail={handleConfirmEmail}
                  isEmailConfirmDisabled={isEmailConfirmDisabled}
                  handleEditProfile={handleEditProfile}
                  loadAvatar={loadAvatar}
                />
              }
            />
            <Route
              path="subscriptions"
              element={<UserProfileSubscriptions handleEditSubscriptions={handleEditSubscriptions} />}
            />
            <Route
              path="change-password"
              element={<UserProfileChangePassword handleChangePassword={handleChangePassword} />}
            />
          </Routes>
        </div>
      )}
    </Spin>
  );
}

export default UserProfileContainer;
